import React from "react";
import {Formik} from "formik";
import {Resident, RESIDENT_STATUS} from "../../page-admin-residents/types";
import {
  FormCard,
  FormSlide,
  GenderField,
  SelectField,
  TextField,
  DateFieldInput,
  FlexRow,
} from "@reside/ui";
import {CreateResidentDto} from "../types";
import {UseMutateFunction} from "react-query";
import {ApiResponse} from "apisauce";
import {validate} from "./validation";
import {CreateFormActionButtons} from "./CreateFormActionButtons";
import {EditFormActionButtons} from "./EditFormActionButtons";
import {PossibleDuplicates} from "./PossibleDuplicates";
import {ResidentFormWrap} from "./ResidentFormWrap";
import {useOrganizationsQuery} from "../../page-admin-user-edit/hooks/useOrganizationsQuery";
import {AppState, select} from "../../../store";
import {connect} from "react-redux";
import {UserDto} from "../../../utils/types/user";
import {isResideAdmin} from "../../../utils/helpers/users";

const Renderer = ({
  initialValues,
  handleSubmit,
  edit = false,
  user,
  isPccResident,
}: {
  initialValues: CreateResidentDto;
  handleSubmit: UseMutateFunction<
    ApiResponse<Resident, Resident>,
    any,
    CreateResidentDto
  >;
  /** Enable component to know if it's in edit or create form  */
  edit?: boolean;
  user: UserDto;
  isPccResident?: boolean;
}): JSX.Element => {
  const isResideAdminUser = isResideAdmin(user);
  const currentUserOrganizationId = user.organization.id;
  const {data: organizations, isLoading: fetchingOrganizations} =
    useOrganizationsQuery();

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={values => handleSubmit(values)}
      validate={(values: CreateResidentDto) =>
        validate(values, isResideAdminUser, edit)
      }
    >
      {({submitForm, dirty}) => {
        return (
          <ResidentFormWrap>
            <FormSlide>
              <FormCard title="Resident Information">
                <TextField label="First Name" name="first_name" />
                <TextField label="Middle Name" name="middle_name" />
                <TextField label="Last Name" name="last_name" />
                <GenderField label="Gender" name="gender" />
                <DateFieldInput
                  label="Date of birth"
                  name="date_of_birth"
                  range={{
                    before: [150, "year"],
                    after: [0, "day"],
                  }}
                />

                {/* Show status select field only if resident is not coming from pcc */}
                {edit && !isPccResident && (
                  <SelectField
                    name="status"
                    label="Status"
                    options={Object.values(RESIDENT_STATUS)}
                    getOptionLabel={option => option}
                    getOptionValue={option => option.toUpperCase()}
                    disabled={!organizations || fetchingOrganizations}
                  />
                )}

                {!edit && (
                  <PossibleDuplicates
                    currentUserOrganizationId={currentUserOrganizationId}
                  />
                )}

                <FlexRow
                  style={{
                    justifyContent: "flex-end",
                    gap: 16,
                    marginTop: 32,
                    // Position button outside of form visually but inside of it functionally
                    position: "absolute",
                    top: -40,
                    right: 0,
                  }}
                >
                  {edit ? (
                    <EditFormActionButtons
                      editFormSubmit={submitForm}
                      disabled={!dirty}
                    />
                  ) : (
                    <CreateFormActionButtons createFormSubmit={submitForm} />
                  )}
                </FlexRow>
              </FormCard>
            </FormSlide>
          </ResidentFormWrap>
        );
      }}
    </Formik>
  );
};

const mapStateToProps = (state: AppState) => ({
  user: select.adminSession.user(state),
});

export const ResidentFormik = connect(mapStateToProps)(Renderer);
